import { TimelineInteractionItem } from '../atoms/timeline-interaction-item';
import { useMemo } from 'react';
import { DateGroupItems } from '../../../shared/components/molecules/date-group/date-group-items';
import { useTimelineScroll } from '../../hooks/timeline/use-timeline-scroll';
import Loading from 'src/modules/shared/components/atoms/loading';
import { useSearchTimelineItems } from '../../hooks/api/timeline-items';
import {
  SearcherDocumentSearchFilterOperator,
  SearcherDocumentSearchSortDirection,
} from 'src/api/generated';
import { TimelineEmptyState } from './timeline-empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useDateGroupItems } from 'src/modules/shared/hooks/date-group-items';
import { getTimelineRows } from '../../utils/timeline/grouping.util';
import { PiCaretDown } from 'react-icons/pi';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useFilteredTimelineItems } from '../../hooks/timeline/filtered-items';
import { TimelineItemListSkeleton } from './timeline-item-list-skeleton';
import { useTimelineNewActivityIndicator } from '../../hooks/timeline/new-activity-indicator';
import { useRegisterConversationView } from 'src/modules/shared/communications/hooks/use-conversation-view';
import { useTimelineNewMessages } from '../../hooks/timeline/use-timeline-new-messages';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TimelineItemListProps } from '../../types/timeline/item-list.types';

/**
 * Renders contact interactions with history loading and newest-activity following.
 * @param props - Contact, conversation, layout boundary and playback selection.
 * @returns The loading, empty or scrollable interaction timeline.
 */
export function TimelineItemList({
  layoutRevision,
  layoutBoundaryRef,
  loadingContact = false,
  contact,
  onPlayRecording,
  activeCallId,
  funnelId,
  conversationId,
}: TimelineItemListProps) {
  const { t } = useI18n();
  // Fetch funnel statuses to resolve status names for status history items
  const statusQuery = useMemo(() => {
    if (!funnelId) return {};

    return createQuery()
      .equals('funnelId', funnelId)
      .orderByAsc('position')
      .build();
  }, [funnelId]);

  const { statuses } = useFunnelStatuses(statusQuery, !!funnelId);

  const timelineItemsQuery = useMemo(() => {
    if (!contact?.id) return {};

    return {
      filters: [
        {
          field: 'contactId',
          operator: SearcherDocumentSearchFilterOperator.EQ,
          value: contact.id.toString(),
        },
      ],
      sort: [
        {
          field: 'createdAt',
          direction: SearcherDocumentSearchSortDirection.DESC,
        },
      ],
      size: 20,
    };
  }, [contact?.id]);

  const {
    timelineItems,
    loading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSearchTimelineItems(timelineItemsQuery, !!contact?.id);

  // Apply post-fetch filtering (e.g. to filter out status items that don't belong to the current conversation)
  const filteredTimelineItems = useFilteredTimelineItems({
    items: timelineItems,
    conversationId,
  });

  const { setContainer, isAtBottom, scrollToBottom } = useTimelineScroll({
    scopeKey: `${conversationId}:${contact?.id}`,
    layoutRevision,
    layoutBoundaryRef,
    items: filteredTimelineItems,
    isFetchingHistory: isFetchingNextPage,
    hasMore: hasNextPage,
    fetchMore: fetchNextPage,
  });
  useRegisterConversationView(
    conversationId === undefined ? null : { conversationId, isAtBottom }
  );
  const newMessages = useTimelineNewMessages({
    items: filteredTimelineItems,
    scopeKey: `${conversationId}:${contact?.id}`,
    ready: !loading && !loadingContact,
    isAtBottom,
  });
  const { hasNewActivity, handleScrollToBottom } =
    useTimelineNewActivityIndicator({
      items: filteredTimelineItems,
      isAtBottom,
      scrollToBottom,
    });

  const { groupedItems: dateGroupedItems, sortedDates } = useDateGroupItems(
    filteredTimelineItems
  );

  if (loading || loadingContact) {
    return <TimelineItemListSkeleton />;
  }

  if (filteredTimelineItems.length === 0) {
    return <TimelineEmptyState />;
  }

  return (
    <div className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
      <div
        className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-5 pb-5 gap-2"
        ref={setContainer}
        role="region"
        tabIndex={-1}
        aria-label="Historial de la conversación"
      >
        {sortedDates.map((date) => (
          <DateGroupItems
            key={date}
            date={new Date(date)}
            itemsClassName="gap-0"
          >
            {getTimelineRows(dateGroupedItems[date]).map(
              ({ item, isContinuation }) => (
                <div
                  key={`${item.modelType}:${item.id}`}
                  className={isContinuation ? 'pt-1' : 'pt-3'}
                  data-timeline-item
                >
                  <TimelineInteractionItem
                    item={item}
                    isContinuation={isContinuation}
                    contact={contact}
                    onPlayRecording={onPlayRecording}
                    activeCallId={activeCallId}
                    statuses={statuses}
                    funnelId={funnelId}
                    conversationId={conversationId}
                  />
                </div>
              )
            )}
          </DateGroupItems>
        ))}
      </div>
      {isFetchingNextPage && (
        <div className="absolute top-0 inset-x-6 flex justify-center py-2 pointer-events-none">
          <Loading className="h-full items-center" />
        </div>
      )}

      {!isAtBottom && (
        <div className="absolute bottom-4 right-6 flex justify-end">
          <Button
            onClick={() => {
              newMessages.clear();
              handleScrollToBottom();
            }}
            size="sm"
            variant="primary"
            appearance="soft"
            className="shadow-md rounded-full border border-primary-300 gap-1 px-2"
          >
            {(hasNewActivity || newMessages.count > 0) && (
              <Text size="tiny" weight="medium">
                {newMessages.count > 0
                  ? t(
                      newMessages.count === 1
                        ? 'communications.timeline.newMessage'
                        : 'communications.timeline.newMessages',
                      { count: newMessages.count }
                    )
                  : t('communications.timeline.newActivity')}
              </Text>
            )}
            <PiCaretDown className="w-4 h-4 m-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
